import React, { useEffect, useState } from 'react';
import { Deal, DealStage } from '../../types';
import { createDeal, fetchContacts, fetchOrganizations, fetchLeads, fetchUsersList, updateDeal } from '../../services/api';

interface Props {
  deal?: Deal | null;
  defaultStage?: DealStage;
  onSaved: (d: Deal) => void;
  onClose: () => void;
}

const STAGES: { value: DealStage; label: string; color: string }[] = [
  { value: 'prospecting',   label: 'Prospecting',   color: '#94a3b8' },
  { value: 'qualification', label: 'Qualification', color: '#3b82f6' },
  { value: 'proposal',      label: 'Proposal',      color: '#f59e0b' },
  { value: 'negotiation',   label: 'Negotiation',   color: '#8b5cf6' },
  { value: 'closed_won',    label: 'Closed Won',    color: '#22c55e' },
  { value: 'closed_lost',   label: 'Closed Lost',   color: '#ef4444' },
];

const DealForm: React.FC<Props> = ({ deal, defaultStage, onSaved, onClose }) => {
  const isEdit = !!deal;

  const [leads, setLeads]         = useState<{id:number;name:string}[]>([]);
  const [contacts, setContacts]   = useState<{id:number;name:string;organization_id?:number|null}[]>([]);
  const [orgs, setOrgs]           = useState<{id:number;name:string}[]>([]);
  const [users, setUsers]         = useState<{id:number;name:string}[]>([]);

  useEffect(() => {
    fetchLeads({ per_page: 200 }).then((r: any) => setLeads(r.data ?? r));
    fetchContacts({ per_page: 200 }).then((r: any) => setContacts(r.data ?? r));
    fetchOrganizations({ per_page: 200 }).then((r: any) => setOrgs(r.data ?? r));
    fetchUsersList().then(setUsers);
  }, []);

  const [form, setForm] = useState({
    title:               deal?.title               ?? '',
    value:               deal?.value               ?? '',
    stage:               deal?.stage               ?? (defaultStage ?? 'prospecting'),
    expected_close_date: deal?.expected_close_date ?? '',
    notes:               deal?.notes               ?? '',
    contact_id:          deal?.contact_id          ?? '',
    organization_id:     deal?.organization_id     ?? '',
    lead_id:             deal?.lead_id             ?? '',
    assigned_to:         deal?.assigned_to         ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const handle = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(f => ({
      ...f,
      [name]: value,
      // Reset contact when org changes so stale selection doesn't persist
      ...(name === 'organization_id' ? { contact_id: '' } : {}),
    }));
    setErrors(errs => ({ ...errs, [name]: '' }));
  };

  const visibleContacts = form.organization_id
    ? contacts.filter(c => c.organization_id === Number(form.organization_id))
    : contacts;

  const setStage = (s: DealStage) => {
    setForm(f => ({ ...f, stage: s }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      const payload = {
        ...form,
        value:           form.value           || 0,
        contact_id:      form.contact_id      || null,
        organization_id: form.organization_id || null,
        lead_id:         form.lead_id         || null,
        assigned_to:     form.assigned_to     || null,
        expected_close_date: form.expected_close_date || null,
      };
      const result = isEdit ? await updateDeal(deal!.id, payload) : await createDeal(payload);
      onSaved(result);
    } catch (err: any) {
      if (err?.response?.data?.errors) {
        const mapped: Record<string, string> = {};
        Object.entries(err.response.data.errors).forEach(([k, v]: any) => {
          mapped[k] = Array.isArray(v) ? v[0] : v;
        });
        setErrors(mapped);
      }
    } finally {
      setSaving(false);
    }
  };

  const currentStage = STAGES.find(s => s.value === form.stage);

  return (
    <div className="modal-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="deal-modal">
        {/* Header */}
        <div className="deal-modal-header" style={{ borderTopColor: currentStage?.color ?? '#4f46e5' }}>
          <div className="deal-modal-header-inner">
            <div>
              <h2 className="deal-modal-title">{isEdit ? 'Edit Deal' : 'New Deal'}</h2>
              <p className="deal-modal-subtitle">{isEdit ? 'Update deal details' : 'Add a new deal to your pipeline'}</p>
            </div>
            <button className="deal-modal-close" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        <form onSubmit={submit} className="deal-modal-body">
          {/* Title + Value */}
          <div className="deal-form-row">
            <div className="deal-form-group">
              <label className="deal-form-label">Deal Title <span className="required">*</span></label>
              <input
                className={`deal-form-input${errors.title ? ' input-error' : ''}`}
                name="title"
                value={form.title}
                onChange={handle}
                placeholder="e.g. Enterprise License Deal"
              />
              {errors.title && <span className="field-error">{errors.title}</span>}
            </div>
            <div className="deal-form-group">
              <label className="deal-form-label">Deal Value (₹/mo)</label>
              <div className="deal-input-prefix-wrap">
                <span className="deal-input-prefix">₹</span>
                <input
                  className="deal-form-input deal-input-prefixed"
                  name="value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.value}
                  onChange={handle}
                  placeholder="0.00"
                />
              </div>
              {errors.value && <span className="field-error">{errors.value}</span>}
            </div>
          </div>

          {/* Stage picker */}
          <div className="deal-form-group">
            <label className="deal-form-label">Pipeline Stage</label>
            <div className="stage-picker">
              {STAGES.map(s => (
                <button
                  key={s.value}
                  type="button"
                  className={`stage-pill${form.stage === s.value ? ' stage-pill-active' : ''}`}
                  style={form.stage === s.value ? { borderColor: s.color, background: s.color + '18', color: s.color } : {}}
                  onClick={() => setStage(s.value)}
                >
                  <span className="stage-pill-dot" style={{ background: s.color }} />
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Close Date + Assigned To */}
          <div className="deal-form-row">
            <div className="deal-form-group">
              <label className="deal-form-label">Expected Close Date</label>
              <input
                className="deal-form-input"
                name="expected_close_date"
                type="date"
                value={form.expected_close_date}
                onChange={handle}
              />
              {errors.expected_close_date && <span className="field-error">{errors.expected_close_date}</span>}
            </div>
            <div className="deal-form-group">
              <label className="deal-form-label">Assigned To</label>
              <select
                className="deal-form-input"
                name="assigned_to"
                value={form.assigned_to}
                onChange={handle}
              >
                <option value="">— Unassigned —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              {errors.assigned_to && <span className="field-error">{errors.assigned_to}</span>}
            </div>
          </div>

          {/* Organization + Contact */}
          <div className="deal-form-row">
            <div className="deal-form-group">
              <label className="deal-form-label">Organization</label>
              <select
                className="deal-form-input"
                name="organization_id"
                value={form.organization_id}
                onChange={handle}
              >
                <option value="">— None —</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              {errors.organization_id && <span className="field-error">{errors.organization_id}</span>}
            </div>
            <div className="deal-form-group">
              <label className="deal-form-label">
                Contact
                {form.organization_id && <span className="deal-form-label-hint"> — filtered by org</span>}
              </label>
              <select
                className="deal-form-input"
                name="contact_id"
                value={form.contact_id}
                onChange={handle}
              >
                <option value="">— None —</option>
                {visibleContacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {form.organization_id && visibleContacts.length === 0 && (
                <span className="deal-form-label-hint">No contacts linked to this organisation yet.</span>
              )}
              {errors.contact_id && <span className="field-error">{errors.contact_id}</span>}
            </div>
          </div>

          {/* Lead */}
          <div className="deal-form-row">
            <div className="deal-form-group">
              <label className="deal-form-label">Lead</label>
              <select
                className="deal-form-input"
                name="lead_id"
                value={form.lead_id}
                onChange={handle}
              >
                <option value="">— None —</option>
                {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              {errors.lead_id && <span className="field-error">{errors.lead_id}</span>}
            </div>
          </div>

          {/* Notes */}
          <div className="deal-form-group">
            <label className="deal-form-label">Notes</label>
            <textarea
              className="deal-form-input"
              name="notes"
              value={form.notes}
              onChange={handle}
              rows={3}
              placeholder="Any additional context or notes…"
            />
          </div>

          {/* Footer */}
          <div className="deal-modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving
                ? <><span className="btn-spinner" />Saving…</>
                : isEdit ? 'Save Changes' : 'Create Deal'
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DealForm;
